import {
  type ApolloError,
  useLazyQuery,
  useMutation,
  useQuery,
  useSuspenseQuery,
} from "@apollo/client";
import {
  AccountCard,
  Alert,
  Button,
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
  CommandLoading,
  CurrencyAmountUI,
  DataTable,
  DataTableColumnHeader,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
  entryFromEventFragment,
  HoverLink,
  Input,
  PaymentIdentifier,
  PaymentIdentifierLabel,
  Popover,
  PopoverContent,
  PopoverTrigger,
  PrimaryFullScreenSpinner,
  Select,
  SelectContent,
  SelectItem,
  SelectSeparator,
  SelectTrigger,
  SelectValue,
  Spinner,
  StatusUI,
  TimeLog,
  Txt,
  toast,
} from "@repo/ui";
import {
  countryToAlpha3,
  currencies,
  formatLocalDateComponents,
  getCountryLists,
  NotFoundError,
  prettyPrintEnum,
} from "@repo/utils";
import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import type { ColumnDef } from "@tanstack/react-table";
import { ChevronDown } from "lucide-react";
import { Suspense, useRef, useState } from "react";
import { RiCloseCircleLine, RiPencilLine } from "react-icons/ri";
import { PaymentIdentifiersCard } from "~/components/BeneficiaryAccount/PaymentIdentifiers";
import { BeneficiaryAccountStatusBadge } from "~/components/BeneficiaryAccount/statusBadge";
import { AdminCurrencycloudBeneficiaryCard } from "~/components/Currencycloud/AdminCurrencycloudBeneficiaryCard";
import { DetailRow, DetailSubRow } from "~/components/DetailRow/DetailRow";
import { AdminEburyBeneficiaryCard } from "~/components/Ebury/AdminEburyBeneficiaryCard";
import { EntityScreeningCard } from "~/components/EntityScreeningCard";
import { InlineSaveStatus } from "~/components/InlineSaveStatus";
import { AdminPageContainer } from "~/components/layout/AdminPageContainer";
import { AdminPageHeader } from "~/components/layout/AdminPageHeader";
import { graphql } from "~/gql/client";
import type {
  BeneficiaryAccountKind,
  BeneficiaryAccountStatus,
  BeneficiaryAccountTransferRowFragment,
  BeneficiaryEntityKind,
  Currency,
} from "~/gql/client/graphql";
import { opsHead, useOpsTitle } from "~/head";

const updateBeneficiaryAccountDetailsDocument = graphql(`
  mutation AdminUpdateBeneficiaryAccountDetails($input: UpdateBeneficiaryAccountDetailsInput!) {
    updateBeneficiaryAccountDetails(input: $input) {
      beneficiaryAccount {
        id
        displayName
        accountName
        country
        currency
        kind
        entityKind
        businessEntity {
          id
          name
        }
        externalBankAccountDataInstitution {
          id
          displayName
          logoUrl
        }
        events {
          ...TimeLogEventEntry
        }
      }
    }
  }
`);

graphql(`
  fragment BeneficiaryAccountAddressFields on BeneficiaryAccount {
    id
    address {
      id
      buildingNumber
      streetName
      city
      state
      postcode
      country
    }
    events {
      ...TimeLogEventEntry
    }
  }
`);

const setBeneficiaryAccountAddressDocument = graphql(`
  mutation AdminSetBeneficiaryAccountAddress($input: SetBeneficiaryAccountAddressInput!) {
    setBeneficiaryAccountAddress(input: $input) {
      beneficiaryAccount {
        ...BeneficiaryAccountAddressFields
      }
    }
  }
`);

const deleteBeneficiaryAccountAddressDocument = graphql(`
  mutation AdminDeleteBeneficiaryAccountAddress($input: DeleteBeneficiaryAccountAddressInput!) {
    deleteBeneficiaryAccountAddress(input: $input) {
      beneficiaryAccount {
        ...BeneficiaryAccountAddressFields
      }
    }
  }
`);

graphql(`
  fragment BeneficiaryAccountTransferRow on Transfer {
    id
    requestedForDate
    currencyAmount {
      currency
      amount
    }
    status
    sourceAsset {
      ...AssetCardFields
    }
  }
`);

const beneficiaryAccountTransfersDocument = graphql(`
  query BeneficiaryAccountTransfers($id: ID!) {
    beneficiaryAccount(id: $id) {
      id
      transfers {
        ...BeneficiaryAccountTransferRow
      }
    }
  }
`);

const transferColumns: ColumnDef<BeneficiaryAccountTransferRowFragment>[] = [
  {
    id: "date",
    accessorFn: (row) => row.requestedForDate,
    header: ({ column }) => (
      <DataTableColumnHeader column={column} title="Date" />
    ),
    cell: ({ row }) => {
      const { shortDate, shortTime } = formatLocalDateComponents(
        row.original.requestedForDate,
      );
      return (
        <div className="flex flex-col">
          <div>{shortDate}</div>
          <div className="text-tertiary text-xs">{shortTime}</div>
        </div>
      );
    },
  },
  {
    id: "sourceAccount",
    accessorFn: (row) => row.sourceAsset,
    header: ({ column }) => (
      <DataTableColumnHeader column={column} title="Source Account" />
    ),
    cell: ({ row }) => {
      const { sourceAsset } = row.original;
      if (!sourceAsset) {
        return "-";
      }
      return (
        <HoverLink to={`/assets/${sourceAsset.id}`}>
          <AccountCard
            account={sourceAsset}
            subText={["BUSINESS_ENTITY", "PROVIDER"]}
          />
        </HoverLink>
      );
    },
  },
  {
    id: "amount",
    accessorFn: (row) => row.currencyAmount?.amount,
    header: ({ column }) => (
      <DataTableColumnHeader column={column} title="Amount" align="right" />
    ),
    cell: ({ row }) => {
      const { currencyAmount } = row.original;
      if (!currencyAmount) {
        return "-";
      }
      return (
        <div className="text-right">
          <CurrencyAmountUI
            currency={currencyAmount.currency}
            amount={currencyAmount.amount}
          />
        </div>
      );
    },
  },
  {
    id: "status",
    accessorFn: (row) => row.status,
    header: ({ column }) => (
      <DataTableColumnHeader column={column} title="Status" />
    ),
    cell: ({ row }) => {
      const { status } = row.original;
      if (!status) {
        return "-";
      }
      return <StatusUI status={status} />;
    },
  },
];

export const Route = createFileRoute(
  "/__auth/beneficiary-accounts_/$beneficiaryAccountId",
)({
  head: () => opsHead("Beneficiary Account"),
  component: () => (
    <Suspense fallback={<PrimaryFullScreenSpinner />}>
      <View />
    </Suspense>
  ),
});

const { commonCountriesList, uncommonCountriesList } = getCountryLists();

function View() {
  const { beneficiaryAccountId } = Route.useParams();
  const [disableAccountOpen, setDisableAccountOpen] = useState(false);
  const [closeAccountOpen, setCloseAccountOpen] = useState(false);
  const [activateAccountOpen, setActivateAccountOpen] = useState(false);
  const [transfersOpen, setTransfersOpen] = useState(false);
  const navigate = useNavigate();
  if (!beneficiaryAccountId) {
    throw new NotFoundError();
  }

  const [updateBeneficiaryAccountStatus, { loading: mutationLoading }] =
    useMutation(
      graphql(`
        mutation UpdateBeneficiaryAccountStatus($id: ID!, $newStatus: BeneficiaryAccountStatus!) {
          updateBeneficiaryAccountStatus(input: {id: $id, newStatus: $newStatus}) {
            beneficiaryAccount {
              id
              status
            }
          }
        }
      `),
    );

  const [setDefaultBeneficiaryAccount, { loading: setDefaultLoading }] =
    useMutation(
      graphql(`
        mutation AdminSetDefaultBeneficiaryAccount($input: SetDefaultBeneficiaryAccountInput!) {
          setDefaultBeneficiaryAccount(input: $input) {
            beneficiaryAccount {
              id
              isDefault
            }
          }
        }
      `),
    );

  const [
    updateBeneficiaryAccountDetails,
    updateBeneficiaryAccountDetailsResult,
  ] = useMutation(updateBeneficiaryAccountDetailsDocument);
  const [setBeneficiaryAccountAddress, setAddressResult] = useMutation(
    setBeneficiaryAccountAddressDocument,
  );
  const [deleteBeneficiaryAccountAddress, deleteAddressResult] = useMutation(
    deleteBeneficiaryAccountAddressDocument,
  );
  const savingFieldRef = useRef<string | null>(null);

  const [
    searchInstitutions,
    { data: institutionSearchData, loading: institutionSearchLoading },
  ] = useLazyQuery(
    graphql(`
      query AdminBeneficiaryAccountInstitutionSearch($searchTerm: String) {
        externalBankAccountDataInstitutions(
          first: 20
          filter: {searchTerm: $searchTerm, active: true}
        ) {
          institutions {
            id
            displayName
            logoUrl
          }
        }
      }
    `),
  );

  const updateAccountStatus = async (newStatus: BeneficiaryAccountStatus) => {
    await updateBeneficiaryAccountStatus({
      variables: {
        newStatus,
        id: beneficiaryAccountId,
      },
      onCompleted() {
        toast({
          title: "Account updated",
          kind: "success",
          autoHide: true,
        });
      },
      onError(e) {
        const description = e.graphQLErrors.at(0)?.message;
        toast({
          title: "Something went wrong",
          kind: "error",
          autoHide: false,
          description,
        });
      },
    });
    setCloseAccountOpen(false);
    setActivateAccountOpen(false);
    setDisableAccountOpen(false);
  };

  const { data, error } = useSuspenseQuery(
    graphql(`
      query AdminBeneficiaryAccount($id: ID!) {
        beneficiaryAccount(id: $id) {
          id
          accountName
          displayName
          currency
          country
          status
          isDefault
          kind
          entityKind
          paymentIdentifiersMatchBankStatement
          organisation {
            id
            name
            businessEntities {
              id
              name
              eburyClient {
                id
                clientName
              }
              currencycloudAccount {
                id
              }
            }
          }
          businessEntity {
            id
            name
          }
          paymentIdentifiers {
            id
            ...PaymentIdentifierComponent
          }
          address {
            id
            buildingNumber
            streetName
            city
            state
            postcode
            country
          }
          externalBankAccountDataInstitution {
            id
            displayName
            logoUrl
          }
          externalBankStatement {
            id
            shortRef
            fileName
            createdAt
            status
            downloadUrl
            paymentIdentifiers {
              id
              ...PaymentIdentifierComponent
            }
          }
          events {
            ...TimeLogEventEntry
          }
          ledgerAccounts {
            id
          }
          externalBankAccountAsset {
            ...AssetCardFields
          }
          ... @defer {
            eburyBeneficiaries {
              id
              businessEntity {
                id
              }
              ...AdminEburyBeneficiaryCard
            }
          }
          ... @defer {
            currencycloudBeneficiaries {
              id
              businessEntity {
                id
              }
              ...AdminCurrencycloudBeneficiaryCard
            }
          }
          screeningResults {
            ...EntityScreeningCardFields
          }
        }
      }
    `),
    { variables: { id: beneficiaryAccountId } },
  );

  const { loading: transfersLoading, data: transfersData } = useQuery(
    beneficiaryAccountTransfersDocument,
    {
      variables: { id: beneficiaryAccountId },
    },
  );

  const transfers = transfersData?.beneficiaryAccount?.transfers ?? [];

  const [editingNickname, setEditingNickname] = useState(false);
  const [editingAccountName, setEditingAccountName] = useState(false);
  const [institutionPickerOpen, setInstitutionPickerOpen] = useState(false);
  const [institutionSearch, setInstitutionSearch] = useState("");
  const [editingKind, setEditingKind] = useState(false);
  const [pendingKind, setPendingKind] =
    useState<BeneficiaryAccountKind>("PAYEE");
  const [pendingBusinessEntityId, setPendingBusinessEntityId] = useState("");
  const emptyAddressForm = {
    buildingNumber: "",
    streetName: "",
    city: "",
    state: "",
    postcode: "",
    country: "",
  };
  const [addressDialogOpen, setAddressDialogOpen] = useState(false);
  const [deleteAddressOpen, setDeleteAddressOpen] = useState(false);
  const [addressForm, setAddressForm] = useState(emptyAddressForm);

  const beneficiaryAccount = data?.beneficiaryAccount;
  if (!beneficiaryAccount) {
    throw new NotFoundError();
  }

  useOpsTitle(`${beneficiaryAccount.displayName} - Beneficiary Account`);

  const onMutationError = (e: ApolloError) => {
    toast({
      title: "Something went wrong updating the account",
      kind: "error",
      autoHide: true,
      description: e.graphQLErrors?.at(0)?.message,
    });
  };

  const updateNickname = async (name: string) => {
    setEditingNickname(false);
    savingFieldRef.current = "nickname";
    await updateBeneficiaryAccountDetails({
      variables: { input: { beneficiaryAccountId, nickname: name } },
      onError: onMutationError,
    });
  };

  const updateAccountName = async (name: string) => {
    setEditingAccountName(false);
    savingFieldRef.current = "accountName";
    await updateBeneficiaryAccountDetails({
      variables: { input: { beneficiaryAccountId, accountName: name } },
      onError: onMutationError,
    });
  };

  const updateInstitution = async (institutionId: string | null) => {
    setInstitutionPickerOpen(false);
    setInstitutionSearch("");
    savingFieldRef.current = "institution";
    await updateBeneficiaryAccountDetails({
      variables: {
        input: {
          beneficiaryAccountId,
          externalBankAccountDataInstitutionId: institutionId,
        },
      },
      onError: onMutationError,
    });
  };

  const updateKind = async () => {
    savingFieldRef.current = "kind";
    await updateBeneficiaryAccountDetails({
      variables: {
        input: {
          beneficiaryAccountId,
          kind: pendingKind,
          businessEntityId:
            pendingKind === "VERIFIED_BENEFICIARY"
              ? pendingBusinessEntityId
              : undefined,
        },
      },
      onCompleted: () => {
        setEditingKind(false);
        toast({ title: "Kind updated", kind: "success", autoHide: true });
      },
      onError: onMutationError,
    });
  };

  const {
    accountName,
    organisation,
    businessEntity,
    currency,
    country,
    id,
    displayName,
    status,
    isDefault,
    eburyBeneficiaries,
    currencycloudBeneficiaries,
    kind,
    entityKind,
    address,
    externalBankAccountDataInstitution,
  } = beneficiaryAccount;
  const externalBankStatement = beneficiaryAccount.externalBankStatement;
  const showPaymentIdentifierWarning =
    status === "PENDING" &&
    beneficiaryAccount.paymentIdentifiersMatchBankStatement === false;

  const openAddressDialog = () => {
    setAddressForm(
      address
        ? {
            buildingNumber: address.buildingNumber,
            streetName: address.streetName,
            city: address.city,
            state: address.state,
            postcode: address.postcode,
            country: countryToAlpha3(address.country),
          }
        : emptyAddressForm,
    );
    setAddressDialogOpen(true);
  };

  const saveAddress = async () => {
    savingFieldRef.current = "address";
    await setBeneficiaryAccountAddress({
      variables: { input: { beneficiaryAccountId, address: addressForm } },
      onCompleted: () => {
        setAddressDialogOpen(false);
        toast({ title: "Address saved", kind: "success", autoHide: true });
      },
      onError: onMutationError,
    });
  };

  const deleteAddress = async () => {
    savingFieldRef.current = "address";
    await deleteBeneficiaryAccountAddress({
      variables: { input: { beneficiaryAccountId } },
      onCompleted: () => {
        setDeleteAddressOpen(false);
        toast({ title: "Address removed", kind: "success", autoHide: true });
      },
      onError: onMutationError,
    });
  };

  const addressFormFields = [
    ["buildingNumber", "Building number"],
    ["streetName", "Street name"],
    ["city", "City"],
    ["state", "State"],
    ["postcode", "Postcode"],
  ] as const;
  const addressFormIncomplete = Object.values(addressForm).some(
    (v) => v.trim() === "",
  );

  const fxBusinessEntities = (organisation?.businessEntities ?? []).filter(
    (be) => be.eburyClient !== null || be.currencycloudAccount !== null,
  );

  return (
    <AdminPageContainer>
      <AdminPageHeader title={displayName} />

      <div className="flex flex-col gap-4">
        {showPaymentIdentifierWarning && (
          <Alert
            kind="warning"
            title="Payment identifiers do not match the bank statement"
            description="Review the linked external bank statement before activating this beneficiary account."
          />
        )}

        {status === "ACTIVE" && (
          <div className="flex flex-row justify-end">
            <Dialog
              onOpenChange={setDisableAccountOpen}
              open={disableAccountOpen}
            >
              <DialogTrigger asChild>
                <Button variant="destructive">Disable account</Button>
              </DialogTrigger>
              <DialogContent>
                <DialogHeader>
                  <DialogTitle>Disable beneficiary account</DialogTitle>
                  <DialogDescription>
                    Are you sure you want to disable this beneficiary account?
                    <br />
                    It will no longer be able to be used for payouts.
                  </DialogDescription>
                </DialogHeader>

                <DialogFooter className="gap-2 sm:justify-between">
                  <Button
                    type="button"
                    variant="secondary"
                    onClick={(e) => {
                      setDisableAccountOpen(false);
                      e.preventDefault();
                    }}
                  >
                    Cancel
                  </Button>
                  <Button
                    disabled={mutationLoading}
                    onClick={() => updateAccountStatus("DISABLED")}
                    variant="destructive"
                  >
                    {mutationLoading ? <Spinner /> : "Disable account"}
                  </Button>
                </DialogFooter>
              </DialogContent>
            </Dialog>
          </div>
        )}

        {status === "PENDING" && (
          <div className="flex flex-row justify-end gap-4">
            <Dialog
              onOpenChange={setActivateAccountOpen}
              open={activateAccountOpen}
            >
              <DialogTrigger asChild>
                <Button>Activate account</Button>
              </DialogTrigger>
              <DialogContent>
                <DialogHeader>
                  <DialogTitle>Activate beneficiary account</DialogTitle>
                  <DialogDescription>
                    Are you sure you want to activate this beneficiary account?
                  </DialogDescription>
                </DialogHeader>

                <Txt variant="card-title">Payment identifiers</Txt>
                {beneficiaryAccount.paymentIdentifiers?.map(
                  (paymentIdentifier) => (
                    <Card key={paymentIdentifier.id}>
                      <CardHeader className="py-2">
                        <CardTitle>
                          <Txt variant="card-title">
                            <PaymentIdentifierLabel
                              paymentIdentifier={paymentIdentifier}
                            />
                          </Txt>
                        </CardTitle>
                      </CardHeader>
                      <CardContent>
                        <PaymentIdentifier
                          paymentIdentifier={paymentIdentifier}
                        />
                      </CardContent>
                    </Card>
                  ),
                )}

                <DialogFooter className="gap-2 sm:justify-between">
                  <Button
                    type="button"
                    variant="secondary"
                    onClick={(e) => {
                      setActivateAccountOpen(false);
                      e.preventDefault();
                    }}
                  >
                    Cancel
                  </Button>
                  <Button
                    disabled={mutationLoading}
                    onClick={() => updateAccountStatus("ACTIVE")}
                  >
                    {mutationLoading ? <Spinner /> : "Activate account"}
                  </Button>
                </DialogFooter>
              </DialogContent>
            </Dialog>

            <Dialog onOpenChange={setCloseAccountOpen} open={closeAccountOpen}>
              <DialogTrigger asChild>
                <Button variant="destructive">Close account</Button>
              </DialogTrigger>
              <DialogContent>
                <DialogHeader>
                  <DialogTitle>Close beneficiary account</DialogTitle>
                  <DialogDescription>
                    Are you sure you want to close this beneficiary account?
                  </DialogDescription>
                </DialogHeader>

                <DialogFooter className="gap-2 sm:justify-between">
                  <Button
                    type="button"
                    variant="secondary"
                    onClick={(e) => {
                      setCloseAccountOpen(false);
                      e.preventDefault();
                    }}
                  >
                    Cancel
                  </Button>
                  <Button
                    disabled={mutationLoading}
                    onClick={() => updateAccountStatus("CLOSED")}
                    loading={mutationLoading}
                    variant="destructive"
                  >
                    Close Account
                  </Button>
                </DialogFooter>
              </DialogContent>
            </Dialog>
          </div>
        )}

        <div className="flex flex-row gap-4">
          <Card className="flex-grow" variant="panel">
            <CardHeader className="flex flex-row items-center justify-between">
              <CardTitle>
                <Txt variant="card-title">Beneficiary account details</Txt>
              </CardTitle>
            </CardHeader>

            <CardContent className="flex flex-col gap-0">
              <DetailRow
                label="Status"
                value={<BeneficiaryAccountStatusBadge status={status} />}
                className=""
              />
              <DetailRow
                label="Default"
                value={
                  isDefault ? (
                    <Txt>Yes</Txt>
                  ) : (
                    <div className="flex items-center gap-3">
                      <Txt>No</Txt>
                      <Button
                        variant="outline"
                        size="sm"
                        loading={setDefaultLoading}
                        onClick={() =>
                          void setDefaultBeneficiaryAccount({
                            variables: {
                              input: {
                                organisationId: organisation?.id ?? "",
                                currency,
                                beneficiaryAccountId,
                              },
                            },
                            onCompleted() {
                              toast({
                                title: "Default account updated",
                                kind: "success",
                                autoHide: true,
                              });
                            },
                            onError(e) {
                              toast({
                                title: "Something went wrong",
                                kind: "error",
                                autoHide: false,
                                description: e.graphQLErrors.at(0)?.message,
                              });
                            },
                          })
                        }
                      >
                        Set as default
                      </Button>
                    </div>
                  )
                }
              />
              <DetailRow
                label="Organisation"
                value={
                  <Link
                    to="/organisations/$organisationId"
                    params={{
                      organisationId: organisation?.id,
                    }}
                    className="underline"
                  >
                    <Txt>{organisation?.name}</Txt>
                  </Link>
                }
              />
              {kind === "VERIFIED_BENEFICIARY" && (
                <DetailRow
                  label="Business Entity"
                  value={
                    <div className="flex items-center gap-2">
                      <Select
                        value={businessEntity?.id ?? "__none__"}
                        onValueChange={(v) => {
                          savingFieldRef.current = "businessEntity";
                          void updateBeneficiaryAccountDetails({
                            variables: {
                              input: {
                                beneficiaryAccountId,
                                businessEntityId: v === "__none__" ? null : v,
                              },
                            },
                            onError: onMutationError,
                          });
                        }}
                      >
                        <SelectTrigger className="h-8 w-fit min-w-[20ch] font-normal">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          {(organisation?.businessEntities ?? []).map((be) => (
                            <SelectItem key={be.id} value={be.id}>
                              <Txt>{be.name}</Txt>
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      {savingFieldRef.current === "businessEntity" && (
                        <InlineSaveStatus
                          mutationResult={updateBeneficiaryAccountDetailsResult}
                        />
                      )}
                    </div>
                  }
                />
              )}
              <DetailRow
                label="Nickname"
                value={
                  editingNickname ? (
                    <div className="flex w-1/3 flex-row items-center gap-2">
                      <Input
                        autoFocus
                        defaultValue={displayName}
                        onKeyDown={(e) => {
                          if (e.key === "Enter") {
                            void updateNickname(e.currentTarget.value);
                          } else if (e.key === "Escape") {
                            setEditingNickname(false);
                          }
                        }}
                      />
                      <RiCloseCircleLine
                        className="size-6 cursor-pointer text-secondary"
                        onClick={() => setEditingNickname(false)}
                      />
                    </div>
                  ) : (
                    <div className="flex flex-row items-center gap-4">
                      <Txt>{displayName}</Txt>
                      <RiPencilLine
                        className="cursor-pointer"
                        onClick={() => setEditingNickname(true)}
                      />
                      {savingFieldRef.current === "nickname" && (
                        <InlineSaveStatus
                          mutationResult={updateBeneficiaryAccountDetailsResult}
                        />
                      )}
                    </div>
                  )
                }
              />
              <DetailRow
                label="Account Name"
                value={
                  editingAccountName ? (
                    <div className="flex w-1/3 flex-row items-center gap-2">
                      <Input
                        autoFocus
                        defaultValue={accountName}
                        onKeyDown={(e) => {
                          if (e.key === "Enter") {
                            void updateAccountName(e.currentTarget.value);
                          } else if (e.key === "Escape") {
                            setEditingAccountName(false);
                          }
                        }}
                      />
                      <RiCloseCircleLine
                        className="size-6 cursor-pointer text-secondary"
                        onClick={() => setEditingAccountName(false)}
                      />
                    </div>
                  ) : (
                    <div className="flex flex-row items-center gap-4">
                      <Txt>{accountName}</Txt>
                      <RiPencilLine
                        className="cursor-pointer"
                        onClick={() => setEditingAccountName(true)}
                      />
                      {savingFieldRef.current === "accountName" && (
                        <InlineSaveStatus
                          mutationResult={updateBeneficiaryAccountDetailsResult}
                        />
                      )}
                    </div>
                  )
                }
              />
              <DetailRow
                label="Currency"
                value={
                  <div className="flex items-center gap-2">
                    <Select
                      value={currency}
                      onValueChange={(v) => {
                        savingFieldRef.current = "currency";
                        void updateBeneficiaryAccountDetails({
                          variables: {
                            input: {
                              beneficiaryAccountId,
                              currency: v as Currency,
                            },
                          },
                          onError: onMutationError,
                        });
                      }}
                    >
                      <SelectTrigger className="h-8 w-fit min-w-[10ch] font-normal">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {currencies.map((c) => (
                          <SelectItem key={c} value={c}>
                            <Txt>{c}</Txt>
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    {savingFieldRef.current === "currency" && (
                      <InlineSaveStatus
                        mutationResult={updateBeneficiaryAccountDetailsResult}
                      />
                    )}
                  </div>
                }
              />
              <DetailRow
                label="Country"
                value={
                  <div className="flex items-center gap-2">
                    <Select
                      value={country}
                      onValueChange={(v) => {
                        savingFieldRef.current = "country";
                        void updateBeneficiaryAccountDetails({
                          variables: {
                            input: { beneficiaryAccountId, country: v },
                          },
                          onError: onMutationError,
                        });
                      }}
                    >
                      <SelectTrigger className="h-8 w-fit min-w-[20ch] font-normal">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {commonCountriesList.map(({ value, label }) => (
                          <SelectItem value={value} key={`common-${value}`}>
                            <Txt>{label}</Txt>
                          </SelectItem>
                        ))}
                        <SelectSeparator />
                        {uncommonCountriesList.map(({ value, label }) => (
                          <SelectItem value={value} key={`uncommon-${value}`}>
                            <Txt>{label}</Txt>
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    {savingFieldRef.current === "country" && (
                      <InlineSaveStatus
                        mutationResult={updateBeneficiaryAccountDetailsResult}
                      />
                    )}
                  </div>
                }
              />
              <DetailRow
                label="Kind"
                value={
                  <div className="flex flex-row items-center gap-4">
                    <Txt>{prettyPrintEnum(kind)}</Txt>
                    <Dialog
                      open={editingKind}
                      onOpenChange={(open) => {
                        setEditingKind(open);
                        if (open) {
                          setPendingKind(kind);
                          setPendingBusinessEntityId(businessEntity?.id ?? "");
                        }
                      }}
                    >
                      <DialogTrigger asChild>
                        <RiPencilLine className="cursor-pointer" />
                      </DialogTrigger>
                      <DialogContent>
                        <DialogHeader>
                          <DialogTitle>
                            Update beneficiary account kind
                          </DialogTitle>
                          <DialogDescription>
                            Verified beneficiary accounts must be linked to a
                            business entity and are always treated as companies.
                            Payees cannot have a business entity.
                          </DialogDescription>
                        </DialogHeader>

                        <div className="flex flex-col gap-4">
                          <div className="flex flex-col gap-2">
                            <Txt variant="field-label">Kind</Txt>
                            <Select
                              value={pendingKind}
                              onValueChange={(v) =>
                                setPendingKind(v as BeneficiaryAccountKind)
                              }
                            >
                              <SelectTrigger className="font-normal">
                                <SelectValue />
                              </SelectTrigger>
                              <SelectContent>
                                <SelectItem value="PAYEE">Payee</SelectItem>
                                <SelectItem value="VERIFIED_BENEFICIARY">
                                  Verified beneficiary
                                </SelectItem>
                              </SelectContent>
                            </Select>
                          </div>
                          {pendingKind === "VERIFIED_BENEFICIARY" && (
                            <div className="flex flex-col gap-2">
                              <Txt variant="field-label">Business entity</Txt>
                              <Select
                                value={pendingBusinessEntityId || undefined}
                                onValueChange={setPendingBusinessEntityId}
                              >
                                <SelectTrigger className="font-normal">
                                  <SelectValue placeholder="Select a business entity" />
                                </SelectTrigger>
                                <SelectContent>
                                  {(organisation?.businessEntities ?? []).map(
                                    (be) => (
                                      <SelectItem key={be.id} value={be.id}>
                                        <Txt>{be.name}</Txt>
                                      </SelectItem>
                                    ),
                                  )}
                                </SelectContent>
                              </Select>
                            </div>
                          )}
                        </div>

                        <DialogFooter className="gap-2 sm:justify-between">
                          <Button
                            type="button"
                            variant="secondary"
                            onClick={() => setEditingKind(false)}
                          >
                            Cancel
                          </Button>
                          <Button
                            disabled={
                              updateBeneficiaryAccountDetailsResult.loading ||
                              pendingKind === kind ||
                              (pendingKind === "VERIFIED_BENEFICIARY" &&
                                !pendingBusinessEntityId)
                            }
                            onClick={() => void updateKind()}
                          >
                            {updateBeneficiaryAccountDetailsResult.loading ? (
                              <Spinner />
                            ) : (
                              "Save"
                            )}
                          </Button>
                        </DialogFooter>
                      </DialogContent>
                    </Dialog>
                    {savingFieldRef.current === "kind" && (
                      <InlineSaveStatus
                        mutationResult={updateBeneficiaryAccountDetailsResult}
                      />
                    )}
                  </div>
                }
              />
              {kind !== "VERIFIED_BENEFICIARY" && (
                <DetailRow
                  label="Entity Kind"
                  value={
                    <div className="flex items-center gap-2">
                      <Select
                        value={entityKind}
                        onValueChange={(v) => {
                          savingFieldRef.current = "entityKind";
                          void updateBeneficiaryAccountDetails({
                            variables: {
                              input: {
                                beneficiaryAccountId,
                                entityKind: v as BeneficiaryEntityKind,
                              },
                            },
                            onError: onMutationError,
                          });
                        }}
                      >
                        <SelectTrigger className="h-8 w-fit min-w-[14ch] font-normal">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="INDIVIDUAL">Individual</SelectItem>
                          <SelectItem value="COMPANY">Company</SelectItem>
                        </SelectContent>
                      </Select>
                      {savingFieldRef.current === "entityKind" && (
                        <InlineSaveStatus
                          mutationResult={updateBeneficiaryAccountDetailsResult}
                        />
                      )}
                    </div>
                  }
                />
              )}
              <DetailRow
                label="Address"
                value={
                  <div className="flex flex-col gap-2">
                    {address ? (
                      <>
                        <DetailSubRow
                          label="Building number"
                          value={address.buildingNumber}
                        />
                        <DetailSubRow
                          label="Street name"
                          value={address.streetName}
                        />
                        <DetailSubRow label="City" value={address.city} />
                        <DetailSubRow label="State" value={address.state} />
                        <DetailSubRow
                          label="Postcode"
                          value={address.postcode}
                        />
                        <DetailSubRow label="Country" value={address.country} />
                        <div className="flex items-center gap-2">
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={openAddressDialog}
                          >
                            Edit
                          </Button>
                          <Button
                            variant="destructive"
                            size="sm"
                            disabled={kind === "PAYEE"}
                            title={
                              kind === "PAYEE"
                                ? "Payees require an address and cannot have it removed"
                                : undefined
                            }
                            onClick={() => setDeleteAddressOpen(true)}
                          >
                            Delete
                          </Button>
                          {savingFieldRef.current === "address" && (
                            <InlineSaveStatus
                              mutationResult={
                                deleteAddressResult.called
                                  ? deleteAddressResult
                                  : setAddressResult
                              }
                            />
                          )}
                        </div>
                      </>
                    ) : (
                      <div className="flex items-center gap-2">
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={openAddressDialog}
                        >
                          Not set
                        </Button>
                      </div>
                    )}
                  </div>
                }
              />
              <Dialog
                open={addressDialogOpen}
                onOpenChange={setAddressDialogOpen}
              >
                <DialogContent>
                  <DialogHeader>
                    <DialogTitle>
                      {address ? "Edit address" : "Set address"}
                    </DialogTitle>
                    <DialogDescription>
                      {address
                        ? "Update the address for this beneficiary account."
                        : "Add an address for this beneficiary account."}
                    </DialogDescription>
                  </DialogHeader>

                  <div className="flex flex-col gap-2">
                    {addressFormFields.map(([field, label]) => (
                      <Input
                        key={field}
                        placeholder={label}
                        value={addressForm[field]}
                        onChange={(e) => {
                          const value = e.target.value;
                          setAddressForm((prev) => ({
                            ...prev,
                            [field]: value,
                          }));
                        }}
                      />
                    ))}
                    <Select
                      value={addressForm.country}
                      onValueChange={(v) => {
                        setAddressForm((prev) => ({
                          ...prev,
                          country: v,
                        }));
                      }}
                    >
                      <SelectTrigger className="font-normal">
                        <SelectValue placeholder="Country" />
                      </SelectTrigger>
                      <SelectContent>
                        {commonCountriesList.map(({ value, label }) => (
                          <SelectItem value={value} key={`common-${value}`}>
                            <Txt>{label}</Txt>
                          </SelectItem>
                        ))}
                        <SelectSeparator />
                        {uncommonCountriesList.map(({ value, label }) => (
                          <SelectItem value={value} key={`uncommon-${value}`}>
                            <Txt>{label}</Txt>
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>

                  <DialogFooter className="gap-2 sm:justify-between">
                    <Button
                      type="button"
                      variant="secondary"
                      onClick={() => setAddressDialogOpen(false)}
                    >
                      Cancel
                    </Button>
                    <Button
                      disabled={
                        setAddressResult.loading || addressFormIncomplete
                      }
                      onClick={() => void saveAddress()}
                    >
                      {setAddressResult.loading ? <Spinner /> : "Save"}
                    </Button>
                  </DialogFooter>
                </DialogContent>
              </Dialog>
              <Dialog
                open={deleteAddressOpen}
                onOpenChange={setDeleteAddressOpen}
              >
                <DialogContent>
                  <DialogHeader>
                    <DialogTitle>Remove address</DialogTitle>
                    <DialogDescription>
                      Are you sure you want to remove the address from this
                      beneficiary account?
                    </DialogDescription>
                  </DialogHeader>
                  <DialogFooter className="gap-2 sm:justify-between">
                    <Button
                      type="button"
                      variant="secondary"
                      onClick={() => setDeleteAddressOpen(false)}
                    >
                      Cancel
                    </Button>
                    <Button
                      variant="destructive"
                      disabled={deleteAddressResult.loading}
                      onClick={() => void deleteAddress()}
                    >
                      {deleteAddressResult.loading ? <Spinner /> : "Remove"}
                    </Button>
                  </DialogFooter>
                </DialogContent>
              </Dialog>
              <DetailRow
                label="Institution"
                value={
                  <div className="flex items-center gap-2">
                    <Popover
                      open={institutionPickerOpen}
                      onOpenChange={(o) => {
                        setInstitutionPickerOpen(o);
                        if (o) {
                          void searchInstitutions({
                            variables: { searchTerm: "" },
                          });
                        } else {
                          setInstitutionSearch("");
                        }
                      }}
                    >
                      <PopoverTrigger asChild>
                        <Button
                          variant="outline"
                          className="h-8 justify-start gap-2 font-normal"
                        >
                          {externalBankAccountDataInstitution ? (
                            <>
                              {externalBankAccountDataInstitution.logoUrl && (
                                <img
                                  src={
                                    externalBankAccountDataInstitution.logoUrl
                                  }
                                  alt=""
                                  className="size-4 rounded-sm object-contain"
                                />
                              )}
                              <span>
                                {externalBankAccountDataInstitution.displayName}
                              </span>
                            </>
                          ) : (
                            <span className="text-tertiary">Not set</span>
                          )}
                        </Button>
                      </PopoverTrigger>
                      <PopoverContent className="w-80 p-0" align="start">
                        <Command shouldFilter={false}>
                          <CommandInput
                            placeholder="Search institutions..."
                            value={institutionSearch}
                            onValueChange={(v) => {
                              setInstitutionSearch(v);
                              void searchInstitutions({
                                variables: { searchTerm: v },
                              });
                            }}
                          />
                          <CommandList>
                            {institutionSearchLoading && <CommandLoading />}
                            <CommandEmpty>No institutions found.</CommandEmpty>
                            <CommandGroup>
                              {externalBankAccountDataInstitution && (
                                <CommandItem
                                  value="__clear__"
                                  onSelect={() => void updateInstitution(null)}
                                  className="text-danger"
                                >
                                  Clear institution
                                </CommandItem>
                              )}
                              {(
                                institutionSearchData
                                  ?.externalBankAccountDataInstitutions
                                  ?.institutions ?? []
                              ).map((institution) => (
                                <CommandItem
                                  key={institution.id}
                                  value={institution.id}
                                  onSelect={() =>
                                    void updateInstitution(institution.id)
                                  }
                                  className="flex items-center gap-2"
                                >
                                  {institution.logoUrl && (
                                    <img
                                      src={institution.logoUrl}
                                      alt=""
                                      className="size-5 rounded-sm object-contain"
                                    />
                                  )}
                                  <span>{institution.displayName}</span>
                                </CommandItem>
                              ))}
                            </CommandGroup>
                          </CommandList>
                        </Command>
                      </PopoverContent>
                    </Popover>
                    {savingFieldRef.current === "institution" && (
                      <InlineSaveStatus
                        mutationResult={updateBeneficiaryAccountDetailsResult}
                      />
                    )}
                  </div>
                }
              />
              <DetailRow label="ID" value={id} />
              {beneficiaryAccount.ledgerAccounts?.[0] && (
                <DetailRow
                  label="Ledger Account"
                  value={
                    <Link
                      to="/ledger/accounts/$ledgerAccountId"
                      params={{
                        ledgerAccountId:
                          beneficiaryAccount.ledgerAccounts[0].id,
                      }}
                      className="underline"
                    >
                      {beneficiaryAccount.ledgerAccounts[0].id}
                    </Link>
                  }
                />
              )}

              <DetailRow
                label="External account"
                value={
                  beneficiaryAccount.externalBankAccountAsset ? (
                    <Link
                      to="/assets/$assetId"
                      params={{
                        assetId: beneficiaryAccount.externalBankAccountAsset.id,
                      }}
                      className="hover:underline"
                    >
                      <AccountCard
                        account={beneficiaryAccount.externalBankAccountAsset}
                        subText={["BALANCE_FULL", "BUSINESS_ENTITY"]}
                      />
                    </Link>
                  ) : (
                    "No external account linked"
                  )
                }
              />
            </CardContent>
          </Card>

          <PaymentIdentifiersCard beneficiaryAccount={beneficiaryAccount} />
        </div>

        {externalBankStatement && (
          <Card variant="panel">
            <CardHeader>
              <CardTitle>
                <Txt variant="card-title">External bank statement</Txt>
              </CardTitle>
            </CardHeader>
            <CardContent className="flex flex-row gap-4">
              {externalBankStatement.downloadUrl ? (
                <div className="h-[720px] flex-1 overflow-hidden rounded-md border">
                  <iframe
                    title={`External bank statement ${externalBankStatement.shortRef}`}
                    loading="lazy"
                    className="size-full"
                    src={`${externalBankStatement.downloadUrl}#navpanes=0`}
                  />
                </div>
              ) : (
                <Txt variant="body" tone="secondary">
                  No statement preview is available.
                </Txt>
              )}
              {!!externalBankStatement.paymentIdentifiers?.length ? (
                <div className="flex flex-col gap-3">
                  {externalBankStatement.paymentIdentifiers.map(
                    (paymentIdentifier) => (
                      <Card key={paymentIdentifier.id}>
                        <CardHeader className="py-2">
                          <CardTitle>
                            <Txt variant="card-title">
                              <PaymentIdentifierLabel
                                paymentIdentifier={paymentIdentifier}
                              />
                            </Txt>
                          </CardTitle>
                        </CardHeader>
                        <CardContent>
                          <PaymentIdentifier
                            paymentIdentifier={paymentIdentifier}
                          />
                        </CardContent>
                      </Card>
                    ),
                  )}
                </div>
              ) : (
                <Txt variant="body" tone="secondary">
                  No payment identifiers found
                </Txt>
              )}
            </CardContent>
          </Card>
        )}

        <div className="flex flex-row gap-4">
          {fxBusinessEntities.length > 0 ? (
            <Card variant="panel">
              <CardHeader>
                <CardTitle>
                  <Txt variant="card-title">FX provider beneficiaries</Txt>
                </CardTitle>
              </CardHeader>
              <CardContent className="flex flex-col gap-4">
                <Txt variant="body">
                  Sync this asset to allow it to be used as a beneficiary in the
                  respective Visa or Ebury clients
                </Txt>
                {fxBusinessEntities.map((be) => {
                  if (be.eburyClient) {
                    const eburyBeneficiary =
                      eburyBeneficiaries?.find(
                        (b) => b.businessEntity?.id === be.id,
                      ) ?? null;
                    return (
                      <div key={be.id} className="flex flex-col gap-2">
                        <div className="font-semibold text-sm">
                          {be.name} (Ebury {be.eburyClient?.id})
                        </div>

                        <AdminEburyBeneficiaryCard
                          eburyBeneficiary={
                            eburyBeneficiaries === undefined
                              ? undefined
                              : eburyBeneficiary
                          }
                          eburyBusinessEntityId={be.id}
                          beneficiaryAccountId={beneficiaryAccount.id}
                          errors={error?.graphQLErrors?.filter((gqlError) => {
                            return (
                              gqlError.path?.at(0) === "beneficiaryAccount" &&
                              gqlError.path?.at(1) === "eburyBeneficiaries"
                            );
                          })}
                        />
                        <hr />
                      </div>
                    );
                  } else if (be.currencycloudAccount) {
                    const currencycloudBeneficiary =
                      currencycloudBeneficiaries?.find(
                        (b) => b.businessEntity?.id === be.id,
                      ) ?? null;

                    return (
                      <div key={be.id} className="flex flex-col gap-2">
                        <div className="font-semibold text-sm">
                          {be.name} (Visa {be.currencycloudAccount?.id})
                        </div>

                        <AdminCurrencycloudBeneficiaryCard
                          currencycloudBeneficiary={currencycloudBeneficiary}
                          currencycloudBusinessEntityId={be.id}
                          beneficiaryAccountId={beneficiaryAccount.id}
                          errors={error?.graphQLErrors?.filter((gqlError) => {
                            return (
                              gqlError.path?.at(0) === "beneficiaryAccount" &&
                              gqlError.path?.at(1) ===
                                "currencycloudBeneficiaries"
                            );
                          })}
                        />
                        <hr />
                      </div>
                    );
                  }
                })}
              </CardContent>
            </Card>
          ) : null}
        </div>

        {transfers.length > 0 && (
          <Collapsible open={transfersOpen} onOpenChange={setTransfersOpen}>
            <Card variant="panel">
              <CollapsibleTrigger asChild>
                <CardHeader className="flex cursor-pointer select-none flex-row items-center justify-between">
                  <CardTitle>Transfers</CardTitle>
                  <ChevronDown
                    className={`size-4 text-secondary transition-transform duration-200 ${transfersOpen ? "rotate-180" : ""}`}
                  />
                </CardHeader>
              </CollapsibleTrigger>
              <CollapsibleContent>
                <CardContent className="pt-0">
                  <DataTable
                    isLoadingData={transfersLoading}
                    data={transfers}
                    columns={transferColumns}
                    hidePagination
                    pageSize={10_000}
                    className="overflow-hidden"
                    onRowClick={(row) => {
                      void navigate({ to: `/transfers/${row.original.id}` });
                    }}
                  />
                </CardContent>
              </CollapsibleContent>
            </Card>
          </Collapsible>
        )}

        <EntityScreeningCard
          screeningResults={beneficiaryAccount.screeningResults ?? []}
          entityId={{ beneficiaryAccountId: beneficiaryAccount.id }}
          entityName={beneficiaryAccount.accountName}
        />

        <Card className="w-full" variant="panel">
          <CardHeader>
            <CardTitle>
              <Txt variant="card-title">Event log</Txt>
            </CardTitle>
          </CardHeader>
          <CardContent className="flex flex-col">
            {beneficiaryAccount.events && (
              <TimeLog
                entries={beneficiaryAccount.events.map(entryFromEventFragment)}
              />
            )}
            {(beneficiaryAccount.events?.length ?? 0) === 0 && (
              <div className="text-center">No events</div>
            )}
          </CardContent>
        </Card>
      </div>
    </AdminPageContainer>
  );
}
